import { useCallback, useEffect, useMemo, useRef } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import {
    getLiteTradeAssetsAction,
    setLiteTradeAccountMetrics,
    updateMarketDataAction,
    updateMarketDataCandleAction,
} from "../../modules/LiteTrade/liteTradeActions.js";
import {
    getLiteTradeConfig,
    getLiteTradeSessionToken,
    getMarketDataCandleWatchers,
    getMarketDataWatchers
} from "../../modules/LiteTrade/liteTradeSelectors.js";
import useWebSocket, { ReadyState } from "react-use-websocket";
import {
    getIsAppVisibility,
    getUserAccountsSelector,
    getUserDefaultAccountCode,
    getUserSelectedAccountCode
} from "../../app/appSelectors.js";
import _ from "lodash";
import { useLiteTradeAccountEvents, useLiteTradeAccountPortfolios } from "../../modules/LiteTrade/liteTradeHooks.js";
import { oppositeSubscriptionName, SOCKET_TYPE_MESSAGES, } from "../../mobileAppCore/mobileAppConstants.js";
import parseAccountName from "../../utils/parseAccountName.js";
import { setUserSelectedAccountCode } from "../../app/appActions.js";
import moment from "moment";

function LiteTradeWSConnector(props) {
    const {
        accounts,
        defaultAccountCode,
        isAppVisibility,
        liteTradeConfig,
        selectedAccountCode,
        sessionToken,

        getLiteTradeAssets,
        marketDataCandleWatchers,
        marketDataWatchers,
        setAccountMetrics,
        setSelectedAccount,
        updateMarketData,
        updateMarketDataCandle,
    } = props;

    const {
        sendMessage,
        lastMessage,
        readyState,
        getWebSocket,
        reconnectCount
    } = useWebSocket(liteTradeConfig.wssUrl, {
        shouldReconnect: (e) => true,
    });
    const { sendMessage: sendMessageMd, lastMessage: lastMessageMd } = useWebSocket(liteTradeConfig.wssMdUrl, {
        shouldReconnect: (e) => true,
    });

    const activeSubscriptionsRef = useRef({});

    const marketDataWatchersRef = useRef([]);
    const marketDataCandleWatchersRef = useRef([]);

    const activeMarketDataSubscriptionRef = useRef({});
    const activeMarketDataCandleSubscriptionRef = useRef({});

    const sendMessageHandler = useCallback((msg) => {
        sendMessage(JSON.stringify({
            ...msg,
            session: sessionToken,
        }));
    }, [sendMessage, sessionToken]);

    const sendMessageMdHandler = useCallback((msg) => {
        sendMessageMd(JSON.stringify({
            ...msg,
            session: sessionToken,
        }));
    }, [sendMessageMd, sessionToken]);

    const { handleGetAccountPortfolio } = useLiteTradeAccountPortfolios();

    const { handleGetAccountEvents } = useLiteTradeAccountEvents();

    const getSubscriptionParams = useCallback((accountCodes, type, payload = {}) => {
        if (!Array.isArray(accountCodes)) {
            accountCodes = [accountCodes];
        }
        const requestId = `${type}-${accountCodes.join('-')}`;

        return {
            type,
            requestId,
            payload: {
                accounts: accountCodes.map(accountCode => `default:${accountCode}`),
                ...payload,
            }
        }
    }, []);

    const updateSubscription = useCallback((accountCode, type, payload = {}) => {
        const subscriptionParams = getSubscriptionParams(accountCode, type, payload);
        if (activeSubscriptionsRef.current?.[type]) {
            sendMessageHandler({
                type: oppositeSubscriptionName[type],
                refRequestId: activeSubscriptionsRef.current[type].requestId,
            });
        }
        activeSubscriptionsRef.current[type] = subscriptionParams;
        sendMessageHandler(subscriptionParams);
    }, [sendMessageHandler, getSubscriptionParams]);

    const handleUpdateMainSubscriptions = useCallback(() => {
        updateSubscription(accounts.map(item => item.name), SOCKET_TYPE_MESSAGES.ACCOUNT_METRICS_OPEN, {
            includePositions: true
        });
        updateSubscription(accounts.map(item => item.name), SOCKET_TYPE_MESSAGES.ACCOUNT_PORTFOLIOS_OPEN);
    }, [accounts, updateSubscription]);

    // New messages handler
    useEffect(() => {
        if (lastMessage !== null) {
            try {
                const data = JSON.parse(lastMessage.data);
                switch (data.type) {
                    case 'PingRequest':
                        sendMessageHandler({
                            type: "Ping",
                        });
                        break;
                    case 'AccountMetrics':
                        data.payload.metrics.forEach(setAccountMetrics);
                        if (data.payload.metrics.length && data.payload.metrics.length < accounts.length) {
                            handleUpdateMainSubscriptions();
                        }
                        break;
                    case 'AccountPortfolios':
                        data.payload.portfolios.forEach(handleGetAccountPortfolio);
                        break;
                    case 'AccountEvents':
                        handleGetAccountEvents(data.payload.accountEvents[0] || {}, data.timestamp);
                        break;
                    default:
                        console.log('[SOCKET] Unknown lastMessage', data);
                }
            } catch (e) {
                console.error('cl e', e);
            }
        }
    }, [lastMessage, sendMessageHandler, setAccountMetrics, handleGetAccountPortfolio, handleGetAccountEvents, accounts, handleUpdateMainSubscriptions]);

    // for Quotes
    const _updateSubscribeMarketData = useCallback(() => {
        if (!sessionToken) return;
        const type = SOCKET_TYPE_MESSAGES.MARKET_DATA_OPEN;
        const requestId = `${type}-${selectedAccountCode}`;
        const symbols = _.uniq(marketDataWatchersRef.current.map(item => item.symbol));

        if (activeMarketDataSubscriptionRef.current.requestId) {
            sendMessageMdHandler({
                type: oppositeSubscriptionName[type],
                refRequestId: activeMarketDataSubscriptionRef.current.requestId,
            });
            activeMarketDataSubscriptionRef.current = {};
        }

        if (!symbols.length || !selectedAccountCode) return;
        const subscriptionParams = {
            type,
            requestId,
            payload: {
                account: `default:${selectedAccountCode}`,
                symbols,
                eventTypes: [{
                    type: "Quote",
                    format: "COMPACT",
                }]
            },
        };
        activeMarketDataSubscriptionRef.current = subscriptionParams;
        sendMessageMdHandler(subscriptionParams);
    }, [sessionToken, selectedAccountCode, sendMessageMdHandler]);

    // for Candles
    const _updateSubscribeMarketDataCandle = useCallback(() => {
        if (!sessionToken) return;
        const type = SOCKET_TYPE_MESSAGES.MARKET_DATA_OPEN;
        const requestId = `candle-${type}-${selectedAccountCode}`;
        const symbols = _.uniq(marketDataCandleWatchersRef.current.map(item => item.symbol));

        if (activeMarketDataCandleSubscriptionRef.current.requestId) {
            sendMessageMdHandler({
                type: oppositeSubscriptionName[type],
                refRequestId: activeMarketDataCandleSubscriptionRef.current.requestId,
            });
            activeMarketDataCandleSubscriptionRef.current = {};
        }
        if (!symbols.length || !selectedAccountCode) return;
        const subscriptionParams = {
            type,
            requestId,
            payload: {
                account: `default:${selectedAccountCode}`,
                symbols,
                eventTypes: [{
                    type: "Candle",
                    format: "COMPACT",
                    candleType: "1d",
                    fromTime: moment().utcOffset(0, true).add(-1, 'week').startOf('day').toISOString(),
                    toTime: moment().utcOffset(0, true).startOf('day').toISOString(),
                }]
            },
        };
        activeMarketDataCandleSubscriptionRef.current = subscriptionParams;
        sendMessageMdHandler(subscriptionParams);
    }, [sessionToken, selectedAccountCode, sendMessageMdHandler]);

    // debounce
    const updateSubscribeMarketData = useMemo(() => _.debounce(_updateSubscribeMarketData, 500), [_updateSubscribeMarketData]);
    const updateSubscribeMarketDataCandle = useMemo(() => _.debounce(_updateSubscribeMarketDataCandle, 500), [_updateSubscribeMarketDataCandle]);

    const reconnectHandler = useCallback(() => {
        getWebSocket().close();
    }, [getWebSocket])

    // Change readyState
    useEffect(() => {
        // TODO Обработать переподписку или сделать разлогин, если пропала связь
        console.log('cl readyState', readyState, 'isAppVisibility', isAppVisibility);
        if (isAppVisibility && readyState === 1) {
            updateSubscribeMarketData();
            updateSubscribeMarketDataCandle();
        }
    }, [readyState, updateSubscribeMarketData, updateSubscribeMarketDataCandle, isAppVisibility]);

    useEffect(() => {
        if (marketDataWatchers) {
            marketDataWatchersRef.current = marketDataWatchers;
            updateSubscribeMarketData();
        }
    }, [marketDataWatchers, updateSubscribeMarketData]);

    useEffect(() => {
        if (marketDataCandleWatchers) {
            marketDataCandleWatchersRef.current = marketDataCandleWatchers;
            updateSubscribeMarketDataCandle();
        }
    }, [marketDataCandleWatchers, updateSubscribeMarketDataCandle]);

    const connectionStatus = {
        [ReadyState.CONNECTING]: 'Connecting',
        [ReadyState.OPEN]: 'Open',
        [ReadyState.CLOSING]: 'Closing',
        [ReadyState.CLOSED]: 'Closed',
        [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
    }[readyState];

    useEffect(() => {
        if (accounts.length && selectedAccountCode) {
            if (!_.find(accounts, { name: selectedAccountCode })) {
                localStorage.removeItem('selectedAccount');
                setSelectedAccount(null);
            }
        }
        if (accounts.length && !selectedAccountCode) {
            const selectedAccountInMem = localStorage.getItem('selectedAccount');
            const foundSelectedAccountInMem = _.find(accounts, { name: selectedAccountInMem });
            const foundDefaultAccount = _.find(accounts, (accountItem) =>
                defaultAccountCode === parseAccountName(accountItem.name)?.accountCode);
            setSelectedAccount(foundSelectedAccountInMem?.name || foundDefaultAccount?.name || accounts[0].name);
        }
    }, [accounts, defaultAccountCode, selectedAccountCode, setSelectedAccount]);

    useEffect(() => {
        if (selectedAccountCode && isAppVisibility && sessionToken) {
            updateSubscription(selectedAccountCode, SOCKET_TYPE_MESSAGES.ACCOUNT_EVENTS_OPEN);
            getLiteTradeAssets();
        }
    }, [selectedAccountCode, updateSubscription, getLiteTradeAssets, isAppVisibility, sessionToken]);

    useEffect(() => {
        if (accounts.length && isAppVisibility && sessionToken) {
            handleUpdateMainSubscriptions()
        }
    }, [accounts, isAppVisibility, handleUpdateMainSubscriptions, sessionToken]);

    // New messages handler from Market Data
    useEffect(() => {
        if (lastMessageMd !== null) {
            const data = JSON.parse(lastMessageMd.data);
            switch (data.type) {
                case 'PingRequest':
                    sendMessageMdHandler({
                        type: "Ping",
                    });
                    break;
                case 'MarketData':
                    if (data.payload.events.length) {
                        if (data.payload.events[0].type === 'Candle') {
                            updateMarketDataCandle(data.payload.events);
                        } else {
                            updateMarketData(data.payload.events);
                        }
                    }
                    break;
                default:
                    console.log('[SOCKET] Unknown lastMessageMd', data);
            }
        }
    }, [lastMessageMd, sendMessageMdHandler, updateMarketData, updateMarketDataCandle]);

    return <>
        {
            (process.env.NODE_ENV !== 'production' || readyState === ReadyState.CLOSED) &&
            <div style={{
                position: 'absolute',
                zIndex: 99999,
                top: '4px',
                right: '4px',
                background: (readyState === ReadyState.OPEN) ? 'var(--color-green)' : 'var(--color-red)',
                width: '8px',
                height: '8px',
                margin: '8px',
                borderRadius: '4px'
                // pointerEvents: 'none'
            }} onClick={reconnectHandler}/>
        }
    </>
}

const mapStateToProps = createStructuredSelector({
    accounts: getUserAccountsSelector,
    defaultAccountCode: getUserDefaultAccountCode,
    isAppVisibility: getIsAppVisibility,
    liteTradeConfig: getLiteTradeConfig,
    marketDataCandleWatchers: getMarketDataCandleWatchers,
    marketDataWatchers: getMarketDataWatchers,
    selectedAccountCode: getUserSelectedAccountCode,
    sessionToken: getLiteTradeSessionToken,
});

const mapDispatchToProps = (dispatch) => ({
    getLiteTradeAssets: payload => dispatch(getLiteTradeAssetsAction(payload)),
    setAccountMetrics: payload => dispatch(setLiteTradeAccountMetrics(payload)),
    setSelectedAccount: payload => dispatch(setUserSelectedAccountCode(payload)),
    updateMarketData: payload => dispatch(updateMarketDataAction(payload)), // Note that this action has been hidden in console by middleware
    updateMarketDataCandle: payload => dispatch(updateMarketDataCandleAction(payload)), // Note that this action has been hidden in console by middleware
});

export default connect(mapStateToProps, mapDispatchToProps)(LiteTradeWSConnector);
